import dayjs, { Dayjs, UnitType } from 'dayjs';
import Duration from 'dayjs/plugin/duration';

import {
  DateRange,
  DurationString,
  OptionalTimeRange,
  TimeUnit,
} from '@cast/shared-types';

dayjs.extend(Duration);

export const includesNow = ([from, to]: DateRange): boolean => {
  const now = dayjs();
  return (
    (now.isAfter(from) || now.isSame(from)) &&
    (now.isBefore(to) || now.isSame(to))
  );
};

/**
 * Returns the length of the provided time range in the specified units.
 */
export const getRangeLength = (
  [from, to]: DateRange,
  units: UnitType = 'milliseconds'
): number => to.diff(from, units);

/**
 * Converts a duration string to a numeric value based on the target unit.
 */
export const durationToUnit = (
  durationStr: DurationString,
  targetUnit: TimeUnit = 's'
): number => {
  const num = parseFloat(durationStr);
  const unit = durationStr.replace(`${num}`, '') as TimeUnit;
  return dayjs.duration({ [unit]: num }).as(targetUnit);
};

/**
 * Returns the value from the provided valuesByDuration object based on the duration of the provided time range.
 * If the time range is invalid or no matching duration is found, returns the fallback value.
 */
export const determineValueByDuration = <T>({
  valuesByDuration,
  range: { from: _from, to: _to },
  fallback,
}: {
  valuesByDuration: Record<DurationString, T>;
  fallback?: T;
  range: OptionalTimeRange<Dayjs | string>;
}): T | undefined => {
  if (!_from || !_to) {
    return;
  }
  const from = dayjs(_from);
  const to = dayjs(_to);

  const totalMilliseconds = to.diff(from, 'millisecond');
  if (totalMilliseconds < 0) {
    return;
  }
  const sortedEntries = Object.entries(valuesByDuration)
    .map(([rangeStr, value]) => ({
      range: durationToUnit(rangeStr as DurationString, 'milliseconds'),
      value,
    }))
    .sort((a, b) => a.range - b.range);

  for (const { range, value } of sortedEntries) {
    if (totalMilliseconds <= range) {
      return value;
    }
  }

  if (fallback !== undefined) {
    return fallback;
  }

  return sortedEntries?.at(-1)?.value;
};

/**
 * Returns the step value from the provided durations object based on the duration of the provided time range.
 * If the time range is invalid or no matching duration is found, returns the fallback value.
 */
export const determineStep = ({
  durations,
  range,
  targetUnit = 'seconds',
  fallback,
}: {
  durations: Record<DurationString, DurationString>;
  range: OptionalTimeRange;
  targetUnit?: TimeUnit;
  fallback?: DurationString;
}): number | undefined => {
  const value = determineValueByDuration({
    valuesByDuration: durations,
    range,
    fallback,
  });
  const result = value ?? fallback;
  if (result) {
    return durationToUnit(result, targetUnit);
  }
  return undefined;
};

type DurationUnit =
  | 'weeks'
  | 'days'
  | 'hours'
  | 'minutes'
  | 'seconds'
  | 'milliseconds'
  | 'microseconds';

const unitLengths: Record<DurationUnit, number> = {
  weeks: 7 * 24 * 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000,
  hours: 60 * 60 * 1000,
  minutes: 60 * 1000,
  seconds: 1000,
  milliseconds: 1,
  microseconds: 1 / 1000,
};

const defaultLabels: Record<DurationUnit, string> = {
  weeks: 'w',
  days: 'd',
  hours: 'h',
  minutes: 'm',
  seconds: 's',
  milliseconds: 'ms',
  microseconds: 'µs',
};

const defaultUnits: DurationUnit[] = [
  'weeks',
  'days',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
  'microseconds',
];

type DurationToTimePartsOptions = {
  unit?: TimeUnit;
  labels?: Partial<Record<DurationUnit, string>>;
  maxDisplayUnit?: DurationUnit;
  minDisplayUnit?: DurationUnit;
  /** Maximum number of units to show, starting with the largest, e.g. for maxGranularity = 2, it will show 2d 12h instead of 2d 12h 30m 10s */
  maxGranularity?: number;
  dropTrailingZeros?: boolean;
};

type DurationTimePart = {
  value: number;
  unit: DurationUnit;
  label: string;
};

export const durationToTimeParts = (
  value: string | number,
  {
    unit = 'seconds',
    labels,
    maxDisplayUnit = 'weeks',
    minDisplayUnit = 'seconds',
    maxGranularity,
    dropTrailingZeros,
  }: DurationToTimePartsOptions = {}
): DurationTimePart[] => {
  const defaultMinUnit =
    minDisplayUnit === 'microseconds' ? 'milliseconds' : minDisplayUnit;
  const defaultPart = {
    value: 0,
    unit: defaultMinUnit,
    label: labels?.[defaultMinUnit] ?? defaultLabels[defaultMinUnit],
  };
  const milliseconds = durationToUnit(`${value}${unit}`, 'ms');
  if (!milliseconds) {
    return [defaultPart];
  }

  let units: DurationUnit[] = [...defaultUnits];

  if (maxDisplayUnit) {
    units = units.splice(units.indexOf(maxDisplayUnit));
  }

  if (minDisplayUnit) {
    units = units.splice(0, units.indexOf(minDisplayUnit) + 1);
  }

  const parts: DurationTimePart[] = [];
  let remaining = milliseconds;

  for (const unit of units) {
    const count = Math.floor(remaining / unitLengths[unit]);
    if (count || (parts.length > 0 && !maxGranularity)) {
      const name = labels?.[unit] ?? defaultLabels[unit];
      parts.push({ value: count, unit, label: name });
      remaining %= unitLengths[unit];
    }
    if (maxGranularity && parts.length >= maxGranularity) {
      break;
    }
  }

  if (dropTrailingZeros) {
    while (parts.at(-1)?.value === 0) {
      parts.pop();
    }
  }

  if (!parts.length) {
    // Math.ceil to avoid showing 0s when the value is less than 1 of the desired unit
    return [{ ...defaultPart, value: 1 }];
  }

  return parts;
};

export const durationToTimeString = (
  value: string | number,
  options: DurationToTimePartsOptions = {}
): string => {
  return durationToTimeParts(value, options)
    .map(({ value, label }) => `${value}${label}`)
    .join(' ');
};
